<template>
  <div class="polygon-controls" v-if="drawnPolygons.length > 0">
    <div class="polygon-controls__header">
      <h4>Drawn Polygons ({{ drawnPolygons.length }})</h4>
    </div>
    <div class="polygon-controls__list">
      <div
        v-for="(polygon, index) in polygonsWithSiteCounts"
        :key="polygon.id"
        class="polygon-item"
        @click="focusOnPolygon(polygon)"
      >
        <div class="polygon-item__info">
          <span class="polygon-id">{{ polygon.id }}</span>
          <span class="polygon-sites">{{ polygon.siteCount }} sites</span>
        </div>
        <button
          class="btn btn-xs btn-danger"
          @click.stop="deletePolygon(index)"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Polygon Actions -->
    <div class="polygon-controls__actions">
      <template v-if="!isEditModeActive && !isDrawModeActive">
        <button
          class="btn btn-primary btn-add-layer"
          @click="activateDrawMode()"
        >
          <i class="fas fa-plus"></i> Add
        </button>
        <button
          class="btn btn-secondary btn-edit-layers"
          @click="activateEditMode()"
        >
          <i class="fas fa-edit"></i> Edit
        </button>
        <button
          class="btn btn-warning btn-delete-layers"
          @click="activateDeleteMode()"
        >
          <i class="fas fa-trash-alt"></i> Delete
        </button>
      </template>

      <template v-else-if="isEditModeActive">
        <div class="edit-mode-actions">
          <button class="btn btn-success btn-apply-edits" @click="saveEdits()">
            <i class="fas fa-check"></i> Apply Changes
          </button>
        </div>
      </template>

      <template v-else-if="isDrawModeActive">
        <div class="draw-mode-actions">
          <button
            class="btn btn-secondary btn-cancel-draw"
            @click="cancelDraw()"
          >
            <i class="fas fa-times"></i> Cancel
          </button>
        </div>
      </template>
    </div>

    <!-- Compose Message Button -->
    <div class="polygon-controls__footer">
      <button
        v-if="composeMessageEnabled"
        class="btn btn-primary btn-compose-message"
        :class="{ 'btn-disabled': isEditModeActive || isDrawModeActive }"
        :disabled="isEditModeActive || isDrawModeActive"
        @click="composeMessage()"
      >
        <i class="fas fa-envelope"></i> Compose a Message
      </button>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";

export default {
  name: "PolygonControls",
  props: {
    mapRef: {
      type: Object,
      required: true,
    },
    drawnItems: {
      type: Object,
      required: true,
    },
    sites: {
      type: Object,
      required: true,
    },
  },
  computed: {
    // Computed property to make site counting reactive
    polygonsWithSiteCounts() {
      return this.drawnPolygons.map((polygon) => {
        const siteCount = this.getSiteCountInPolygon(polygon);
        return {
          ...polygon,
          siteCount: siteCount,
        };
      });
    },
    config() {
      return this.$store.getters.config;
    },
    composeMessageEnabled() {
      console.log("Checking if compose message is enabled");
      return (
        this.config &&
        this.config.optionalFeatures &&
        this.config.optionalFeatures.composeMessage
      );
    },
  },
  data() {
    return {
      drawnPolygons: [],
      polygonIdCounter: 0,
      drawControl: null,
      isEditModeActive: false,
      isDrawModeActive: false,
      totalSitesInPolygons: 0,
    };
  },
  mounted() {
    this.initializeDrawingControls();
  },
  beforeUnmount() {
    this.cleanupDrawingControls();
  },
  methods: {
    initializeDrawingControls() {
      if (!this.mapRef || !this.drawnItems) return;

      // Set up drawing controls
      this.drawControl = new L.Control.Draw({
        position: "topleft",
        draw: {
          polygon: {
            allowIntersection: false,
            drawError: {
              color: "#e1e100",
              message: "<strong>Error:</strong> Shape edges cannot cross!",
            },
            shapeOptions: {
              color: "#3388ff",
              fillColor: "#3388ff",
              fillOpacity: 0.2,
              weight: 3,
              opacity: 0.8,
            },
          },
          circle: false,
          rectangle: false,
          polyline: false,
          marker: false,
          circlemarker: false,
        },
        edit: {
          featureGroup: this.drawnItems,
          remove: true
        }
      });

      this.mapRef.addControl(this.drawControl);

      // Set up event listeners for drawing
      this.mapRef.on(L.Draw.Event.CREATED, this.onPolygonDrawn);
      this.mapRef.on(L.Draw.Event.DELETED, this.onPolygonDeleted);
      this.mapRef.on(L.Draw.Event.EDITED, this.onPolygonEdited);
    },

    cleanupDrawingControls() {
      if (this.mapRef && this.drawControl) {
        this.mapRef.removeControl(this.drawControl);
        this.mapRef.off(L.Draw.Event.CREATED, this.onPolygonDrawn);
        this.mapRef.off(L.Draw.Event.DELETED, this.onPolygonDeleted);
        this.mapRef.off(L.Draw.Event.EDITED, this.onPolygonEdited);
      }
    },

    onPolygonDrawn(event) {
      const layer = event.layer;
      const latLngs = layer
        .getLatLngs()[0]
        .map((latLng) => [latLng.lat, latLng.lng]);
      const layerId = L.Util.stamp(layer); // Get unique Leaflet layer ID

      const polygon = {
        id: `polygon_${this.polygonIdCounter++}`,
        layerId: layerId, // Store layer ID for matching during edits
        latLngs: latLngs,
        style: {
          color: "#3388ff",
          fillColor: "#3388ff",
          fillOpacity: 0.2,
          weight: 3,
          opacity: 0.8,
        },
        metadata: {
          createdAt: new Date().toISOString(),
          area: this.calculatePolygonArea(latLngs),
        },
      };

      this.drawnPolygons.push(polygon);

      // Add the layer to the drawnItems layer group for editing
      this.drawnItems.addLayer(layer);

      // Update site count
      this.updateSiteCount();

      // Deactivate draw mode since polygon was successfully created
      this.isDrawModeActive = false;

      const polySites = this.getSitesInPolygons();
      this.$emit("polygon-drawn", polygon, polySites);
    },

    onPolygonDeleted(event) {
      const layers = event.layers;
      layers.eachLayer((layer) => {
        // Find and remove the corresponding polygon from our data
        const polygonIndex = this.drawnPolygons.findIndex((p) => {
          const layerLatLngs = layer
            .getLatLngs()[0]
            .map((latLng) => [latLng.lat, latLng.lng]);
          return JSON.stringify(p.latLngs) === JSON.stringify(layerLatLngs);
        });

        if (polygonIndex >= 0) {
          const deletedPolygon = this.drawnPolygons.splice(polygonIndex, 1)[0];

          // Update site count
          this.updateSiteCount();

          this.$emit(
            "polygon-deleted",
            deletedPolygon,
            this.getSitesInPolygons()
          );
        }
      });
    },

    onPolygonEdited(event) {
      const layers = event.layers;
      layers.eachLayer((layer) => {
        // Find and update the corresponding polygon in our data using layer ID
        const layerId = L.Util.stamp(layer);
        const layerLatLngs = layer
          .getLatLngs()[0]
          .map((latLng) => [latLng.lat, latLng.lng]);

        const polygonIndex = this.drawnPolygons.findIndex(
          (p) => p.layerId === layerId
        );

        if (polygonIndex >= 0) {
          // Use Vue.set to ensure reactivity
          const updatedPolygon = {
            ...this.drawnPolygons[polygonIndex],
            latLngs: layerLatLngs,
            metadata: {
              ...this.drawnPolygons[polygonIndex].metadata,
              area: this.calculatePolygonArea(layerLatLngs),
              lastModified: new Date().toISOString(),
            },
          };

          // Replace the polygon to trigger Vue reactivity
          this.$set(this.drawnPolygons, polygonIndex, updatedPolygon);

          this.$emit(
            "polygon-edited",
            updatedPolygon,
            this.getSitesInPolygons()
          );
        } else {
          // Fallback to the old proximity-based matching
          this.fallbackPolygonMatching(layerLatLngs);
        }
      });
    },

    // Fallback method for polygon matching when layer ID fails
    fallbackPolygonMatching(layerLatLngs) {
      const polygonIndex = this.drawnPolygons.findIndex((p) => {
        if (!p.latLngs || p.latLngs.length === 0) return false;

        // Check if at least some points are close (indicating it's the same polygon being edited)
        const closePoints = layerLatLngs.filter((newPoint) => {
          return p.latLngs.some((oldPoint) => {
            const distance = Math.sqrt(
              Math.pow(newPoint[0] - oldPoint[0], 2) +
                Math.pow(newPoint[1] - oldPoint[1], 2)
            );
            return distance < 0.1; // Points within ~11km are considered "close"
          });
        });

        // If at least 30% of points are close, it's probably the same polygon
        return (
          closePoints.length >=
          Math.min(3, Math.floor(layerLatLngs.length * 0.3))
        );
      });

      if (polygonIndex >= 0) {
        const updatedPolygon = {
          ...this.drawnPolygons[polygonIndex],
          latLngs: layerLatLngs,
          metadata: {
            ...this.drawnPolygons[polygonIndex].metadata,
            area: this.calculatePolygonArea(layerLatLngs),
            lastModified: new Date().toISOString(),
          },
        };

        this.$set(this.drawnPolygons, polygonIndex, updatedPolygon);

        // Update site count
        this.updateSiteCount();

        this.$emit("polygon-edited", updatedPolygon, this.getSitesInPolygons());
      }
    },

    onPolygonClick(event) {
      const polygon = event.target.options.polygon;
      const polygonIndex = event.target.options.polygonIndex;
      const siteCount = this.getSiteCountInPolygon(polygon);

      // Remove the swal dialog for now
    },

    onPolygonRightClick(event) {
      event.originalEvent.preventDefault();
      const polygonIndex = event.target.options.polygonIndex;
      this.deletePolygon(polygonIndex);
    },

    deletePolygon(index) {
      if (index >= 0 && index < this.drawnPolygons.length) {
        const deletedPolygon = this.drawnPolygons.splice(index, 1)[0];

        // Find and remove the corresponding layer from the map
        this.drawnItems.eachLayer((layer) => {
          const layerLatLngs = layer
            .getLatLngs()[0]
            .map((latLng) => [latLng.lat, latLng.lng]);
          if (
            JSON.stringify(layerLatLngs) ===
            JSON.stringify(deletedPolygon.latLngs)
          ) {
            this.drawnItems.removeLayer(layer);
          }
        });
        this.$emit(
          "polygon-deleted",
          deletedPolygon,
          this.getSitesInPolygons()
        );
      }
    },

    calculatePolygonArea(latLngs) {
      // Simple area calculation using the shoelace formula
      // Note: This is an approximation and doesn't account for Earth's curvature
      let area = 0;
      const n = latLngs.length;

      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += latLngs[i][0] * latLngs[j][1];
        area -= latLngs[j][0] * latLngs[i][1];
      }

      area = Math.abs(area) / 2.0;

      // Convert to approximate km² (very rough approximation)
      // 1 degree ≈ 111 km at equator
      const kmPerDegree = 111;
      return area * kmPerDegree * kmPerDegree;
    },

    // Force clear all polygons without confirmation (for programmatic use)
    forceClearAllPolygons() {
      if (this.drawnPolygons.length > 0) {
        // Clear the polygon data
        this.drawnPolygons = [];

        // Clear all layers from the drawnItems layer group
        this.drawnItems.clearLayers();

        // Emit the polygons-cleared event
        this.$emit("polygons-cleared");
      }
    },

    // Activate draw mode for adding new polygons
    activateDrawMode() {
      try {
        // Get the polygon draw handler from the draw control
        let drawHandler = null;

        if (
          this.drawControl &&
          this.drawControl._toolbars &&
          this.drawControl._toolbars.draw
        ) {
          if (
            this.drawControl._toolbars.draw._modes &&
            this.drawControl._toolbars.draw._modes.polygon
          ) {
            drawHandler = this.drawControl._toolbars.draw._modes.polygon
              .handler;
          }
        }

        if (drawHandler && typeof drawHandler.enable === "function") {
          if (!drawHandler._enabled) {
            drawHandler.enable();
            this.isDrawModeActive = true;
            return true;
          } else {
            this.isDrawModeActive = true;
            return true;
          }
        } else {
          // Fallback: try to trigger draw button click programmatically
          const drawButton = document.querySelector(
            ".leaflet-draw-draw-polygon"
          );
          if (drawButton) {
            drawButton.click();
            this.isDrawModeActive = true;
            return true;
          }
        }
      } catch (error) {
        // Final fallback: try to trigger draw button click programmatically
        try {
          const drawButton = document.querySelector(
            ".leaflet-draw-draw-polygon"
          );
          if (drawButton) {
            drawButton.click();
            this.isDrawModeActive = true;
            return true;
          }
        } catch (fallbackError) {
          // Fallback failed as well
        }
      }

      return false;
    },

    // Cancel draw mode
    cancelDraw() {
      try {
        // Get the polygon draw handler from the draw control
        let drawHandler = null;

        if (
          this.drawControl &&
          this.drawControl._toolbars &&
          this.drawControl._toolbars.draw
        ) {
          if (
            this.drawControl._toolbars.draw._modes &&
            this.drawControl._toolbars.draw._modes.polygon
          ) {
            drawHandler = this.drawControl._toolbars.draw._modes.polygon
              .handler;
          }
        }

        if (drawHandler && typeof drawHandler.disable === "function") {
          drawHandler.disable();
        } else {
          // Fallback: try to trigger cancel button click programmatically
          const cancelButton = document.querySelector(
            '.leaflet-draw-actions-bottom a[title="Cancel drawing"]'
          );
          if (cancelButton) {
            cancelButton.click();
          }
        }

        this.isDrawModeActive = false;
      } catch (error) {
        this.isDrawModeActive = false;
      }
    },

    // Activate edit mode for existing polygons
    activateEditMode() {
      if (this.drawControl && this.drawnPolygons.length > 0) {

        try {
          // Get the edit handler from the draw control
          // Check different possible paths for the edit handler
          let editHandler = null;

          if (this.drawControl._toolbars && this.drawControl._toolbars.edit) {
            if (
              this.drawControl._toolbars.edit._modes &&
              this.drawControl._toolbars.edit._modes.edit
            ) {
              editHandler = this.drawControl._toolbars.edit._modes.edit.handler;
            }
          }

          // Alternative path for different Leaflet Draw versions
          if (
            !editHandler &&
            this.drawControl.options &&
            this.drawControl.options.edit
          ) {
            // Try to manually create edit handler
            const L = window.L;
            if (L && L.EditToolbar && L.EditToolbar.Edit) {
              editHandler = new L.EditToolbar.Edit(this.mapRef, {
                featureGroup: this.drawnItems,
              });
            }
          }

          if (editHandler && typeof editHandler.enable === "function") {
            if (!editHandler._enabled) {
              editHandler.enable();
              this.isEditModeActive = true;
              return true;
            } else {
              this.isEditModeActive = true;
              return true;
            }
          } else {
            // Fallback: try to trigger edit button click programmatically
            const editButton = document.querySelector(
              ".leaflet-draw-edit-edit"
            );
            if (editButton) {
              editButton.click();
              this.isEditModeActive = true;
              return true;
            }
          }
        } catch (error) {

          // Fallback: try to trigger edit button click programmatically
          try {
            const editButton = document.querySelector(
              ".leaflet-draw-edit-edit"
            );
            if (editButton) {
              editButton.click();
              this.isEditModeActive = true;
              return true;
            }
          } catch (fallbackError) {
            console.error(
              "Fallback edit activation also failed:",
              fallbackError
            );
          }
        }
      } else {
        console.log("No polygons to edit or draw control not available");
      }
      return false;
    },

    // Deactivate edit mode
    deactivateEditMode() {
      if (this.drawControl) {
        try {
          // Try to find edit handler
          let editHandler = null;

          if (this.drawControl._toolbars && this.drawControl._toolbars.edit) {
            if (
              this.drawControl._toolbars.edit._modes &&
              this.drawControl._toolbars.edit._modes.edit
            ) {
              editHandler = this.drawControl._toolbars.edit._modes.edit.handler;
            }
          }

          if (
            editHandler &&
            typeof editHandler.disable === "function" &&
            editHandler._enabled
          ) {
            editHandler.disable();
            this.isEditModeActive = false;
          } else {
            // Fallback: try to click edit button again to toggle off
            const editButton = document.querySelector(
              ".leaflet-draw-edit-edit"
            );
            if (
              editButton &&
              editButton.classList.contains(
                "leaflet-draw-toolbar-button-enabled"
              )
            ) {
              editButton.click();
              this.isEditModeActive = false;
            }
          }
        } catch (error) {
          console.error("Error deactivating edit mode:", error);
        }
      }
    },

    // Save edits and exit edit mode
    saveEdits() {
      try {
        let editHandler = null;
        let saveSuccessful = false;

        if (this.drawControl._toolbars && this.drawControl._toolbars.edit) {
          if (
            this.drawControl._toolbars.edit._modes &&
            this.drawControl._toolbars.edit._modes.edit
          ) {
            editHandler = this.drawControl._toolbars.edit._modes.edit.handler;
          }
        }

        if (editHandler && editHandler._enabled) {
          // Try Leaflet's built-in save first
          if (typeof editHandler.save === "function") {
            editHandler.save();
            saveSuccessful = true;
          }

          // Disable edit mode
          editHandler.disable();
        } else {
          // Fallback: try to trigger the save button
          const saveButton = document.querySelector(
            '.leaflet-draw-actions a[title*="Save"], .leaflet-draw-actions a[title*="save"]'
          );
          if (saveButton) {
            saveButton.click();
            saveSuccessful = true;
          } else {
            this.deactivateEditMode();
          }
        }

        this.isEditModeActive = false;
      } catch (error) {
        this.isEditModeActive = false;
      }
    },

    focusOnPolygon(polygon) {
      if (this.mapRef && polygon.latLngs && polygon.latLngs.length > 0) {
        // Calculate bounds of the polygon
        const lats = polygon.latLngs.map((point) => point[0]);
        const lngs = polygon.latLngs.map((point) => point[1]);

        const bounds = [
          [Math.min(...lats), Math.min(...lngs)],
          [Math.max(...lats), Math.max(...lngs)],
        ];

        // Fit the map view to the polygon bounds with some padding
        this.mapRef.fitBounds(bounds, { padding: [20, 20] });

        // Optionally, highlight the polygon temporarily
        this.drawnItems.eachLayer((layer) => {
          const layerLatLngs = layer
            .getLatLngs()[0]
            .map((latLng) => [latLng.lat, latLng.lng]);
          if (
            JSON.stringify(layerLatLngs) === JSON.stringify(polygon.latLngs)
          ) {
            // Temporarily change the style to highlight
            const originalStyle = layer.options;
            layer.setStyle({ color: "#ff0000", weight: 5 });

            // Reset style after 2 seconds
            setTimeout(() => {
              layer.setStyle(originalStyle);
            }, 2000);
          }
        });
      }
    },

    // Point-in-polygon algorithm using ray casting
    isPointInPolygon(lat, lon, polygonLatLngs) {
      let inside = false;
      const x = lon;
      const y = lat;

      for (
        let i = 0, j = polygonLatLngs.length - 1;
        i < polygonLatLngs.length;
        j = i++
      ) {
        const xi = polygonLatLngs[i][1]; // longitude
        const yi = polygonLatLngs[i][0]; // latitude
        const xj = polygonLatLngs[j][1]; // longitude
        const yj = polygonLatLngs[j][0]; // latitude

        if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
          inside = !inside;
        }
      }

      return inside;
    },

    // Check if a lat-lon point is inside any of the drawn polygons
    checkPointInPolygons(lat, lon) {
      const results = [];

      for (let i = 0; i < this.drawnPolygons.length; i++) {
        const polygon = this.drawnPolygons[i];
        const isInside = this.isPointInPolygon(lat, lon, polygon.latLngs);

        if (isInside) {
          results.push({
            polygonId: polygon.id,
            polygonIndex: i,
            polygon: polygon,
          });
        }
      }

      return results;
    },

    // Check if a specific site is inside any polygon
    checkSiteInPolygons(siteKey) {
      if (!this.sites || !this.sites[siteKey]) {
        console.warn(`Site '${siteKey}' not found in sites object`);
        return [];
      }

      const site = this.sites[siteKey];
      const lat = site.lat || site.latitude;
      const lon = site.lon || site.longitude || site.lng;

      if (lat === undefined || lon === undefined) {
        console.warn(`Site '${siteKey}' does not have valid coordinates`);
        return [];
      }

      return this.checkPointInPolygons(lat, lon);
    },

    // Count sites in a specific polygon (reuses existing site checking logic)
    getSiteCountInPolygon(polygon) {
      if (!this.sites || !polygon || !polygon.id || !polygon.latLngs) {
        console.log("getSiteCountInPolygon: Missing data", {
          sites: !!this.sites,
          polygon: !!polygon,
          polygonId: polygon ? polygon.id : "N/A",
          latLngs: polygon ? !!polygon.latLngs : false,
        });
        return 0;
      }

      
      let count = 0;
      Object.keys(this.sites).forEach((siteKey) => {
        const site = this.sites[siteKey];
        if (site) {
          // Use same coordinate access logic as checkSiteInPolygons
          const lat = site.lat || site.latitude;
          const lon = site.lon || site.longitude || site.lng;

          if (lat !== undefined && lon !== undefined) {
            // Check if this specific site is in this specific polygon
            const isInside = this.isPointInPolygon(lat, lon, polygon.latLngs);
            if (isInside) {
              count++;
            }
          }
        }
      });
      return count;
    },

    // Get all sites that are inside any polygon
    getSitesInPolygons() {
      const sitesInPolygons = {};

      if (!this.sites) {
        return sitesInPolygons;
      }

      Object.keys(this.sites).forEach((siteKey) => {
        const results = this.checkSiteInPolygons(siteKey);
        if (results.length > 0) {
          sitesInPolygons[siteKey] = {
            site: this.sites[siteKey],
            polygons: results,
          };
        }
      });

      return sitesInPolygons;
    },

    // Public methods for external access
    getDrawnPolygons() {
      return this.drawnPolygons;
    },

    setDrawnPolygons(polygons) {
      this.drawnPolygons = polygons;
    },

    // Update the total count of sites in all polygons
    updateSiteCount() {
      const sitesInPolygons = this.getSitesInPolygons();
      this.totalSitesInPolygons = Object.keys(sitesInPolygons).length;
    },

    // Activate delete mode for existing polygons
    // Delete all polygons
    activateDeleteMode() {
      if (this.drawnPolygons.length === 0) {
        return;
      }

      // Confirm deletion with user
      swal({
        title: "Delete All Polygons?",
        text:
          "This will permanently delete all drawn polygons. This action cannot be undone.",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Cancel",
            value: null,
            visible: true,
            className: "",
            closeModal: true,
          },
          confirm: {
            text: "Delete All",
            value: true,
            visible: true,
            className: "btn-danger",
            closeModal: true,
          },
        },
        dangerMode: true,
      }).then((willDelete) => {
        if (willDelete) {
          this.deleteAllPolygons();
        }
      });
    },

    // Delete all polygons from the map and data
    deleteAllPolygons() {
      try {
        // Clear all layers from the drawn items layer group
        if (this.drawnItems) {
          this.drawnItems.clearLayers();
        }

        // Clear the polygons array
        this.drawnPolygons = [];

        // Update site count
        this.totalSitesInPolygons = 0;

        // Emit event to parent component
        this.$emit("polygons-updated", {
          polygons: this.drawnPolygons,
          siteCount: this.totalSitesInPolygons,
        });
        // Show success message
        swal({
          title: "Success!",
          text: "All polygons have been deleted.",
          icon: "success",
          button: "OK",
        });
      } catch (error) {
        console.error("Error deleting all polygons:", error);
        swal({
          title: "Error!",
          text: `Failed to delete polygons: ${error.message}`,
          icon: "error",
          button: "OK",
        });
      }
    },

    // Compose message for sites in polygons
    composeMessage() {
      // Get all sites that are inside any polygon
      const sitesInPolygons = this.getSitesInPolygons();
      const siteCount = Object.keys(sitesInPolygons).length;

      if (siteCount === 0) {
        // You could show a notification here
        return;
      }
      // Emit event to parent component to open the compose message modal
      this.$emit("compose-message", {
        sites: sitesInPolygons,
        polygons: this.drawnPolygons,
        siteCount: siteCount,
      });
    },
  },
};
</script>

<style scoped>
.polygon-controls {
  position: absolute;
  top: 100px;
  right: 10px;
  background: white;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.65);
  max-width: 280px;
  z-index: 1000;
  font-family: Arial, sans-serif;
}

.polygon-controls__header {
  padding: 12px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 5px 5px 0 0;
}

.polygon-controls__header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
  color: #333;
}

.polygon-controls__list {
  max-height: 220px;
  overflow-y: auto;
}

.polygon-controls__actions {
  padding: 12px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  display: flex;
  gap: 8px;
}

.btn-add-layer {
  flex: 1;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid #007bff;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-add-layer:hover {
  background-color: #0069d9;
  border-color: #0062cc;
}

.btn-edit-layers {
  flex: 1;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid #6c757d;
  background-color: #6c757d;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-edit-layers:hover {
  background-color: #5a6268;
  border-color: #5a6268;
}

.btn-delete-layers {
  flex: 1;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid #ffc107;
  background-color: #ffc107;
  color: #212529;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-delete-layers:hover {
  background-color: #e0a800;
  border-color: #e0a800;
}

.btn-save-edits {
  flex: 1;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid #28a745;
  background-color: #28a745;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-save-edits:hover {
  background-color: #218838;
  border-color: #218838;
}

.btn-cancel-edits {
  flex: 1;
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid #6c757d;
  background-color: #6c757d;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel-edits:hover {
  background-color: #5a6268;
  border-color: #5a6268;
}

.polygon-controls__footer {
  padding: 12px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 0 0 5px 5px;
}

.btn-compose-message {
  width: 100%;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  border: none;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-compose-message:hover {
  background-color: #0056b3;
}

.btn-compose-message:active {
  background-color: #004085;
}

.btn-compose-message i {
  font-size: 12px;
}

.polygon-item {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.polygon-item:hover {
  background-color: #f8f9fa;
}

.polygon-item:last-child {
  border-bottom: none;
}

.polygon-item__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.polygon-id {
  font-weight: bold;
  font-size: 12px;
  color: #333;
}

.polygon-sites {
  font-size: 11px;
  color: #666;
}

.btn {
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  padding: 4px 8px;
  transition: background-color 0.2s;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 10px;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #c82333;
}

.fas {
  margin-right: 4px;
}

.btn-xs .fas {
  margin-right: 0;
}

/* Edit mode actions styling */
.edit-mode-actions {
  width: 100%;
}

/* Draw mode actions styling */
.draw-mode-actions {
  width: 100%;
}

.btn-cancel-draw {
  width: 100%;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #6c757d;
  border: 2px solid #6c757d;
  color: white;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(108, 117, 125, 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-cancel-draw:hover {
  background-color: #5a6268;
  border-color: #5a6268;
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.4);
  transform: translateY(-1px);
}

.btn-cancel-draw:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(108, 117, 125, 0.3);
}

.btn-cancel-draw .fas {
  font-size: 16px;
  margin-right: 0;
}

.btn-apply-edits {
  width: 100%;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: 2px solid #28a745;
  color: white;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(40, 167, 69, 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-apply-edits:hover {
  background: linear-gradient(135deg, #218838 0%, #1abc9c 100%);
  border-color: #218838;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
  transform: translateY(-1px);
}

.btn-apply-edits:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(40, 167, 69, 0.3);
}

.btn-apply-edits .fas {
  font-size: 16px;
  margin-right: 0;
}

/* Disabled button styling */
.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  color: #fff !important;
}

.btn-disabled:hover {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  transform: none !important;
  box-shadow: none !important;
}
</style>
